import React from "react";
import { IconChevronUp, IconLinkOff, IconSearch } from "@tabler/icons-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import { searchStore } from "features/ProductSearch/SearchStore";
import { observer } from "mobx-react-lite";
import BulkActionBarMenu from "./BulkActionBarMenu";
import useBulkActionBar from "./useBulkActionBar";
import NoRulesPopup from "features/RulesSet/components/NoRulesPopup";
import ConfirmModal from "components/ConfirmModal";
import ComponentsUpdateResponsePopup from "./ComponentsUpdateResponsePopup";
import EditObjectPopup from "./EditObjectPopup";
import { dynamicTableStore } from "../../../DynamicTable/DynamicTableStore.ts";
import { OperationType } from "api-client";

const BulkActionBar = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const { handleOpenProductSearch, handleMapping } = useBulkActionBar();

  React.useEffect(() => {
    MEStore.selectedRows.length === 0 && MEStore.setOpenBulkActionsMenu(false);
  }, [MEStore.selectedRows.length === 0]);

  React.useEffect(() => {
    searchStore.openProductSearch.state === false &&
      MEStore.setMatchForSelectedItems(false);
  }, [searchStore.openProductSearch.state]);

  const getSelectedObjectsTotalCount = () => {
    const total = MEStore.selectedRows
      .map((item) => item?.object_count)
      .filter(Boolean)
      .reduce((acc, value) => Number(acc) + Number(value), 0);
    return total ?? 0;
  };

  const getTitleValues = () => ({
    amount: MEStore.selectAllChecked
      ? t("mappingEditor.all")
      : MEStore.selectedRows.length,
    letter: MEStore.selectedRows.length === 1 ? "" : isEng ? "s" : "e",
  });

  const getGroupingTitleValues = () => ({
    amount: MEStore.selectAllChecked
      ? t("mappingEditor.all")
      : getSelectedObjectsTotalCount(),
    letter: getSelectedObjectsTotalCount() === 1 ? "" : isEng ? "s" : "e",
    groupCount: MEStore.selectedRows.length,
    letter2: MEStore.selectedRows.length === 1 ? "" : isEng ? "s" : "n",
  });

  const updateOutdatedMappings = async () => {
    await handleMapping("update_outdated_mappings");
  };

  const onCloseOutdatedComponentsPopup = () => {
    MEStore.setOpenOutdatedComponentsPopup({
      open: false,
      items: [],
      count: 0,
    });
  };

  const onMouseEnterActionBar = () => {
    MEStore.setOpenBulkActionsMenu(true);
    MEStore.closeEditObjectPopup();
  };

  const onMouseLeaveActionBar = () => {
    const timeout = setTimeout(() => {
      MEStore.setOpenBulkActionsMenu(false);
      clearTimeout(timeout);
    }, 200);
  };

  if (MEStore.selectedRows.length === 0) return <></>;
  return (
    <>
      <div
        className={clsx(
          "h-10 flex items-center justify-center absolute bottom-14 bg-indigo-600 text-sm",
          "shadow-lg w-fit rounded-[20px] text-white text-base z-3 h-10"
        )}
      >
        <div
          className="flex items-center px-3 h-full hover:bg-indigo-700 rounded-tl-full rounded-bl-full"
          onMouseEnter={onMouseEnterActionBar}
          onMouseLeave={onMouseLeaveActionBar}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t(
                dynamicTableStore.filterStore.hasGrouping()
                  ? "mappingEditor.actionBarTextGrouped"
                  : "mappingEditor.actionBarText",
                dynamicTableStore.filterStore.hasGrouping()
                  ? getGroupingTitleValues()
                  : getTitleValues()
              ),
            }}
          />
          <IconChevronUp className="ml-2 cursor-pointer" />
          {MEStore.openBulkActionsMenu && (
            <BulkActionBarMenu className="-translate-x-1/2 left-1/2" />
          )}
        </div>
        <div className="text-white flex border-l border-l-white h-full items-center">
          <IconLinkOff
            size={24}
            className={clsx(
              "cursor-pointer hover:bg-indigo-700 p-2 min-h-[40px] min-w-[40px]",
              {
                "cursor-not-allowed pointer-events-none opacity-50":
                  MEStore.isCurrentFileMappingInProgress(),
              }
            )}
            onClick={() => handleMapping(OperationType.UndoMatch)}
          />
          <IconSearch
            className="p-2 cursor-pointer hover:bg-indigo-700 min-h-[40px] min-w-[40px] rounded-tr-full rounded-br-full"
            onClick={handleOpenProductSearch}
          />
        </div>
        <NoRulesPopup />
        <ConfirmModal
          open={MEStore.openUpdateMappingsModal}
          close={() => MEStore.setOpenUpdateMappingsModal(false)}
          title={t("mappingEditor.updateMappingsTitle")}
          onConfirm={updateOutdatedMappings}
          confirmButtonTitle={t("mappingEditor.updateMappingsConfirm")}
          descriptionMesage={t("mappingEditor.updateMappingsDescription")}
          containerClassName="!min-w-[600px]"
        />
        <ComponentsUpdateResponsePopup
          open={MEStore.outdatedComponents.open}
          close={onCloseOutdatedComponentsPopup}
          components={MEStore.outdatedComponents.items ?? []}
          affectedCount={MEStore.outdatedComponents.count ?? 0}
        />
      </div>
      {MEStore.openEditObjectPopup.state && <EditObjectPopup />}
    </>
  );
};

export default observer(BulkActionBar);
